import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, Wand2, ShieldCheck, AlertTriangle, Send, Edit3, Eye, RefreshCw, Loader2, Calendar, TrendingUp, ArrowLeft, ListChecks, Plug, Map, Code2 } from "lucide-react";
import { safeHtml, stripInlineJsonLd } from "../lib/sanitize";
import { renderBlogContent } from "../lib/renderBlogContent";
import "../styles/blog-content.css";  // Permanent .virat-blog theme — same stylesheet used by BlogDetailPage so admin preview matches live 100%.

const API = process.env.REACT_APP_BACKEND_URL;

const scoreColor = (v) => {
  if (v >= 85) return "text-emerald-300 bg-emerald-500/10 border-emerald-500/30";
  if (v >= 65) return "text-gold bg-gold/10 border-gold/30";
  if (v >= 45) return "text-amber-300 bg-amber-500/10 border-amber-500/30";
  return "text-rose-300 bg-rose-500/10 border-rose-500/30";
};

export default function AutoBlogEnginePage() {
  const navigate = useNavigate();
  const [engineReady, setEngineReady] = useState(false);
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelection, setBulkSelection] = useState(new Set());
  const [bulkQueue, setBulkQueue] = useState(null);
  const [bulkPolling, setBulkPolling] = useState(false);

  const [form, setForm] = useState({
    topic: "",
    free_text_extras: "",
    planetary_placement_json: "",
    include_rashi_section: true,
    category: "Astrology",
  });
  const [activeDraft, setActiveDraft] = useState(null);

  // Load engine status + suggestions + existing drafts
  const loadDashboard = useCallback(async () => {
    try {
      const [statusResp, suggResp, draftResp] = await Promise.all([
        axios.get(`${API}/api/blogs/auto-blog/status`, { withCredentials: true }),
        axios.get(`${API}/api/blogs/auto-blog/topic-suggestions?limit=15`, { withCredentials: true }),
        axios.get(`${API}/api/blogs/auto-blog/drafts?limit=25`, { withCredentials: true }),
      ]);
      setEngineReady(statusResp.data?.status === "ready");
      setWelcomeMsg(statusResp.data?.message || "");
      setSuggestions(suggResp.data?.suggestions || []);
      setDrafts(draftResp.data?.drafts || []);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("कृपया लॉग इन करें");
        navigate("/admin/login");
      } else {
        toast.error("Dashboard load failed");
      }
    }
  }, [navigate]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleGenerate = async () => {
    if (!form.topic || form.topic.trim().length < 8) {
      toast.error("Topic कम-से-कम 8 characters का होना चाहिए");
      return;
    }
    setGenerating(true);
    setActiveDraft(null);
    try {
      let planetaryPayload = null;
      if (form.planetary_placement_json?.trim()) {
        try {
          planetaryPayload = JSON.parse(form.planetary_placement_json);
        } catch {
          planetaryPayload = form.planetary_placement_json.trim();
        }
      }
      const payload = {
        topic: form.topic.trim(),
        free_text_extras: form.free_text_extras?.trim() || null,
        planetary_placement: planetaryPayload,
        include_rashi_section: form.include_rashi_section,
        category: form.category || "Astrology",
      };

      // Enqueue async job — returns { job_id } in <1s (Cloudflare-safe)
      const enqueueRes = await axios.post(
        `${API}/api/blogs/auto-generate-v2`,
        payload,
        { withCredentials: true, timeout: 15000 }
      );
      const jobId = enqueueRes.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      toast.info("Generation शुरू — यह 60-120s ले सकता है। Please wait...");

      // Poll job status every 4s, up to 4 minutes
      const startedAt = Date.now();
      const MAX_MS = 4 * 60 * 1000;
      let job = null;
      while (Date.now() - startedAt < MAX_MS) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const poll = await axios.get(
            `${API}/api/blogs/auto-generate-v2/status/${jobId}`,
            { withCredentials: true, timeout: 15000 }
          );
          job = poll.data;
          if (job.status === "completed" || job.status === "failed") break;
          // Optional: surface progress
          if (job.status === "running") {
            const secs = Math.round((Date.now() - startedAt) / 1000);
            if (secs % 20 === 0) toast.info(`Still generating... ${secs}s elapsed`);
          }
        } catch (pollErr) {
          console.warn("poll failed:", pollErr.message);
        }
      }

      if (!job) throw new Error("Job timed out after 4 minutes");
      if (job.status === "failed") {
        toast.error(job.error?.slice(0, 200) || "Generation failed");
        return;
      }
      if (job.status !== "completed") {
        toast.warning(`Job still ${job.status} — check drafts in a minute`);
        return;
      }

      const data = job.result;
      setActiveDraft(data);
      if (data.publish_blocked) {
        toast.warning(`Draft तैयार — पर Fact Validator ने ${data.validation?.errors?.length || 0} issues पकड़ीं। Publish blocked।`);
      } else {
        toast.success(`Draft तैयार (${job.duration_seconds}s) — Preview + Approve करें`);
      }
      loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (draftOrId, scheduled_at = null) => {
    const blogId = typeof draftOrId === "string" ? draftOrId : draftOrId?.blog_id;
    if (!blogId) return;
    try {
      const { data } = await axios.post(
        `${API}/api/blogs/auto-blog/${blogId}/approve`,
        { scheduled_at },
        { withCredentials: true }
      );
      toast.success(scheduled_at ? `Scheduled for ${scheduled_at}` : "Published live");
      setActiveDraft(null);
      loadDashboard();
      return data;
    } catch (e) {
      const errText = e.response?.data?.detail?.message || e.response?.data?.detail || "Approve failed";
      toast.error(typeof errText === "string" ? errText : "Fact Validator failed — देखें errors");
    }
  };

  const handleRevalidate = async (blogId) => {
    try {
      const { data } = await axios.post(
        `${API}/api/blogs/auto-blog/${blogId}/revalidate`,
        {},
        { withCredentials: true }
      );
      toast[data.validation?.is_valid ? "success" : "warning"](
        `Revalidated — ${data.validation?.errors?.length || 0} errors, ${data.validation?.warnings?.length || 0} warnings`
      );
      loadDashboard();
    } catch {
      toast.error("Revalidate failed");
    }
  };

  const openDraftPreview = async (blogId) => {
    try {
      const { data } = await axios.get(
        `${API}/api/blogs/auto-blog/${blogId}`,
        { withCredentials: true }
      );
      setActiveDraft({
        blog_id: data.id,
        publish_blocked: !data.validation?.is_valid,
        validation: data.validation,
        preview: {
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content_html: data.content,
          category: data.category,
          jsonld: Array.isArray(data.jsonld) ? data.jsonld : [],
        },
        approved_at: data.approved_at,
        published: data.published,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Preview load failed");
    }
  };

  const toggleBulk = (id) => {
    const next = new Set(bulkSelection);
    if (next.has(id)) next.delete(id); else next.add(id);
    setBulkSelection(next);
  };

  const startBulkGenerate = async () => {
    if (bulkSelection.size === 0) { toast.error("कम-से-कम 1 topic select करें"); return; }
    const topics = suggestions
      .filter((s) => bulkSelection.has(s.id))
      .map((s) => ({
        topic: s.title,
        category: s.category === "advanced" || s.category === "bhava" ? "Astrology" :
                  s.category.charAt(0).toUpperCase() + s.category.slice(1),
        include_rashi_section: !["dosha", "remedy", "advanced"].includes(s.category),
      }));
    try {
      const { data } = await axios.post(
        `${API}/api/blogs/auto-blog/bulk-generate`,
        { topics },
        { withCredentials: true }
      );
      toast.success(`Bulk queue started — ${data.total} topics processing`);
      setBulkQueue({ id: data.queue_id, total: data.total, status: "queued",
                      done_count: 0, success_count: 0, failed_count: 0, items: [] });
      setBulkPolling(true);
      setBulkSelection(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk queue failed");
    }
  };

  // Poll active bulk queue
  useEffect(() => {
    if (!bulkPolling || !bulkQueue?.id) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/blogs/auto-blog/bulk-queue/${bulkQueue.id}`,
          { withCredentials: true }
        );
        setBulkQueue(data);
        if (["done", "failed", "cancelled"].includes(data.status)) {
          setBulkPolling(false);
          loadDashboard();
          toast[data.status === "done" ? "success" : "warning"](
            `Bulk: ${data.success_count}/${data.total} succeeded, ${data.failed_count} failed`
          );
        }
      } catch { /* transient network hiccup, keep polling */ }
    }, 8000);
    return () => clearInterval(interval);
  }, [bulkPolling, bulkQueue?.id, loadDashboard]);

  const bulkApprove = async () => {
    if (!bulkQueue?.id) return;
    try {
      const { data } = await axios.post(
        `${API}/api/blogs/auto-blog/bulk-approve`,
        { queue_id: bulkQueue.id },
        { withCredentials: true }
      );
      toast.success(`${data.approved_count} published, ${data.skipped_count} skipped`);
      loadDashboard();
    } catch { toast.error("Bulk approve failed"); }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-24" data-testid="auto-blog-engine-page">
      {/* Header */}
      <header className="border-b border-gold/20 bg-[#0a1120]/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-2 text-gold/80 hover:text-gold text-sm"
            data-testid="btn-back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Admin Dashboard
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" />
            <h1 className="text-lg sm:text-xl font-heading text-gold">
              ViratAstro Auto Blog Engine
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/integrations")}
              className="text-xs text-gold/80 hover:text-gold flex items-center gap-1"
              data-testid="nav-integrations"
            >
              <Plug className="w-3.5 h-3.5" /> Integrations
            </button>
            <button
              onClick={() => navigate("/admin/roadmap")}
              className="text-xs text-gold/80 hover:text-gold flex items-center gap-1"
              data-testid="nav-roadmap"
            >
              <Map className="w-3.5 h-3.5" /> Roadmap
            </button>
            <div className="text-xs text-gold/70">v3</div>
          </div>
        </div>
      </header>

      {engineReady && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <div className="p-4 rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent"
               data-testid="engine-ready-banner">
            <p className="text-gold text-[15px] sm:text-base">🪔 {welcomeMsg}</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid md:grid-cols-3 gap-6">
        {/* LEFT: Generation Form */}
        <section className="md:col-span-2 space-y-6">
          <div className="rounded-2xl border border-gold/25 bg-[#0a1120]/60 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Wand2 className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-gold text-lg">नया Blog Generate करें</h2>
            </div>

            <label className="block text-sm text-gold/80 mb-1">Topic <span className="text-rose-400">*</span></label>
            <input
              type="text"
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="जैसे — 'शनि का मीन राशि में गोचर 2026 — सम्पूर्ण फल'"
              className="w-full px-3 py-2 rounded-lg bg-[#0F172A] border border-gold/30 text-white text-sm focus:border-gold outline-none mb-4"
              data-testid="input-topic"
            />

            <label className="block text-sm text-gold/80 mb-1">Free-Text Context (optional)</label>
            <textarea
              rows="2"
              value={form.free_text_extras}
              onChange={(e) => setForm({ ...form, free_text_extras: e.target.value })}
              placeholder="कोई additional context, event date, विशेष निर्देश आदि..."
              className="w-full px-3 py-2 rounded-lg bg-[#0F172A] border border-gold/30 text-white text-sm focus:border-gold outline-none mb-4"
              data-testid="input-extras"
            />

            <label className="block text-sm text-gold/80 mb-1">Planetary Placement — JSON या Free-Text (optional)</label>
            <textarea
              rows="3"
              value={form.planetary_placement_json}
              onChange={(e) => setForm({ ...form, planetary_placement_json: e.target.value })}
              placeholder='जैसे — {"Mars": {"house": 7, "sign": "Scorpio", "dignity": "Debilitated"}} या free text'
              className="w-full px-3 py-2 rounded-lg bg-[#0F172A] border border-gold/30 text-white text-sm font-mono focus:border-gold outline-none mb-4"
              data-testid="input-placement"
            />

            <div className="flex flex-wrap items-center gap-4 mb-4">
              <label className="flex items-center gap-2 text-sm text-gold/80">
                <input
                  type="checkbox"
                  checked={form.include_rashi_section}
                  onChange={(e) => setForm({ ...form, include_rashi_section: e.target.checked })}
                  className="w-4 h-4 accent-gold"
                  data-testid="checkbox-rashi"
                />
                12 राशियों का section include करें
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gold/80">Category:</span>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="px-2 py-1 rounded-lg bg-[#0F172A] border border-gold/30 text-white text-sm"
                  data-testid="select-category"
                >
                  {["Astrology", "Puja", "Career", "Gemstone", "Vastu", "Kundli", "Horoscope"].map((c) =>
                    <option key={c}>{c}</option>
                  )}
                </select>
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-gold to-amber-500 text-[#0F172A] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 hover:brightness-110 transition"
              data-testid="btn-generate"
            >
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating (30-60s)...</> :
                <><Sparkles className="w-4 h-4" /> Draft Generate करें</>}
            </button>
          </div>

          {/* Preview of Active Draft */}
          {activeDraft && (
            <div className="rounded-2xl border border-gold/30 bg-[#0a1120]/60 p-5" data-testid="draft-preview">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-gold" />
                <h2 className="font-heading text-gold text-lg">Draft Preview: {activeDraft.preview?.title}</h2>
              </div>

              <ValidationPanel validation={activeDraft.validation} />
              <P0ReviewCard draft={activeDraft} />

              {/* JSON-LD Schema Panel — shows extracted schemas as metadata,
                  NOT as visible body text. This is what search engines see in <head>. */}
              {Array.isArray(activeDraft.preview?.jsonld) && activeDraft.preview.jsonld.length > 0 && (
                <div className="mt-3 rounded-xl border border-blue-500/25 bg-blue-500/5 p-3" data-testid="jsonld-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <Code2 className="w-4 h-4 text-blue-300" />
                    <span className="text-blue-200 text-sm font-medium">JSON-LD Schemas ({activeDraft.preview.jsonld.length})</span>
                    <span className="text-xs text-text-muted">— injected in &lt;head&gt;, hidden from body</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {activeDraft.preview.jsonld.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-blue-500/15 text-blue-200 border border-blue-500/25">
                        {s['@type'] || 'Schema'}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 border border-gold/20 rounded-xl overflow-hidden max-h-[620px] overflow-y-auto"
                   data-testid="draft-html-preview">
                {activeDraft.preview?.title && (
                  <div className="px-4 sm:px-6 pt-6 pb-4 border-b border-gold/15 bg-gradient-to-b from-[#0F172A] to-[#0F172A]/70">
                    <h1 className="blog-hero-title" data-testid="draft-h1-title">
                      {activeDraft.preview.title}
                    </h1>
                    {activeDraft.preview.excerpt && (
                      <p className="mt-3 text-sm text-text-muted leading-relaxed">
                        {activeDraft.preview.excerpt}
                      </p>
                    )}
                    {/* Feb 22, 2026 — Meta SEO fields preview */}
                    {(activeDraft.preview.meta_title || activeDraft.preview.meta_description) && (
                      <div className="mt-4 pt-3 border-t border-gold/10 space-y-2" data-testid="draft-meta-seo">
                        {activeDraft.preview.meta_title && (
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-gold/70">Meta Title ({activeDraft.preview.meta_title.length} chars)</div>
                            <div className={`text-xs ${activeDraft.preview.meta_title.length >= 55 && activeDraft.preview.meta_title.length <= 65 ? 'text-emerald-300' : 'text-amber-300'}`} data-testid="draft-meta-title">
                              {activeDraft.preview.meta_title}
                            </div>
                          </div>
                        )}
                        {activeDraft.preview.meta_description && (
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-gold/70">Meta Description ({activeDraft.preview.meta_description.length} chars)</div>
                            <div className={`text-xs ${activeDraft.preview.meta_description.length >= 140 && activeDraft.preview.meta_description.length <= 165 ? 'text-emerald-300' : 'text-amber-300'}`} data-testid="draft-meta-description">
                              {activeDraft.preview.meta_description}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div
                  className="p-4 sm:p-6 bg-[#0F172A] prose prose-invert max-w-none blog-content"
                  dangerouslySetInnerHTML={safeHtml(
                    renderBlogContent(stripInlineJsonLd(activeDraft.preview?.content_html || ""))
                  )}
                />
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => handleApprove(activeDraft)}
                  disabled={activeDraft.publish_blocked}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/80 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium"
                  data-testid="btn-approve-publish"
                >
                  <Send className="w-4 h-4" /> Approve & Publish
                </button>
                <button
                  onClick={() => {
                    const dt = prompt("Schedule ISO date-time (UTC, e.g. 2026-03-15T09:00:00+00:00):");
                    if (dt) handleApprove(activeDraft, dt);
                  }}
                  disabled={activeDraft.publish_blocked}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/80 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-medium"
                  data-testid="btn-approve-schedule"
                >
                  <Calendar className="w-4 h-4" /> Schedule
                </button>
                <button
                  onClick={() => handleRevalidate(activeDraft.blog_id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-gold hover:bg-gold/10 text-sm"
                  data-testid="btn-revalidate"
                >
                  <RefreshCw className="w-4 h-4" /> Revalidate
                </button>
                <a
                  href={`/blog/${activeDraft.preview?.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-gold hover:bg-gold/10 text-sm"
                  data-testid="btn-open-live"
                >
                  <Eye className="w-4 h-4" /> Live URL
                </a>
              </div>
            </div>
          )}

          {/* Existing Drafts */}
          <div className="rounded-2xl border border-gold/25 bg-[#0a1120]/60 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Edit3 className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-gold text-lg">पुराने Drafts / Published ({drafts.length})</h2>
            </div>
            {drafts.length === 0 && (
              <p className="text-white/60 text-sm">कोई draft नहीं। ऊपर से नया generate करें।</p>
            )}
            <ul className="space-y-2" data-testid="drafts-list">
              {drafts.map((d) => (
                <li key={d.id} className="p-3 rounded-lg border border-gold/15 hover:border-gold/40 bg-[#0F172A]/60 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{d.title}</p>
                    <p className="text-white/50 text-xs mt-1">
                      {d.published ? "🟢 Live" : d.scheduled_at ? `⏳ Scheduled ${d.scheduled_at}` : "📝 Draft"}
                      {" · "}
                      <span className={d.validation?.is_valid ? "text-emerald-400" : "text-rose-400"}>
                        {d.validation?.is_valid ? "✓ Validated" : `✗ ${d.validation?.errors?.length || 0} errors`}
                      </span>
                      {" · "}{new Date(d.created_at).toLocaleDateString("hi-IN")}
                    </p>
                  </div>
                  <button
                    onClick={() => openDraftPreview(d.id)}
                    className="text-gold text-xs hover:underline shrink-0"
                    data-testid={`btn-open-draft-${d.id}`}
                  >
                    Preview →
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* RIGHT: Topic Suggestions */}
        <aside className="space-y-4">
          {/* Bulk Queue Banner (if active) */}
          {bulkQueue && (
            <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/15 to-transparent p-4"
                 data-testid="bulk-queue-banner">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-gold text-sm">🌊 Bulk Queue — {bulkQueue.status}</h3>
                <span className="text-xs text-white/60">{bulkQueue.done_count}/{bulkQueue.total}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-2">
                <div className="h-full bg-gradient-to-r from-gold to-amber-400 transition-all"
                     style={{ width: `${(bulkQueue.done_count / Math.max(bulkQueue.total, 1)) * 100}%` }} />
              </div>
              <p className="text-[11px] text-white/60">✓ {bulkQueue.success_count} succeeded · ✗ {bulkQueue.failed_count} failed</p>
              {bulkQueue.status === "done" && bulkQueue.success_count > 0 && (
                <button onClick={bulkApprove}
                        className="mt-2 w-full py-1.5 rounded-full bg-emerald-500/80 text-white text-xs font-medium hover:bg-emerald-500"
                        data-testid="btn-bulk-approve">
                  🚀 Bulk Approve All ({bulkQueue.success_count})
                </button>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-gold/25 bg-[#0a1120]/60 p-5 sticky top-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gold" />
                <h2 className="font-heading text-gold text-lg">Suggested Topics</h2>
              </div>
              <button
                onClick={() => { setBulkMode(!bulkMode); setBulkSelection(new Set()); }}
                className={`text-[11px] px-2 py-1 rounded-full border ${
                  bulkMode ? "bg-gold text-[#0F172A] border-gold" : "border-gold/40 text-gold hover:bg-gold/10"
                }`}
                data-testid="btn-toggle-bulk-mode"
              >
                <ListChecks className="w-3 h-3 inline mr-1" /> {bulkMode ? "Exit Bulk" : "Bulk Mode"}
              </button>
            </div>

            {bulkMode && bulkSelection.size > 0 && (
              <button
                onClick={startBulkGenerate}
                disabled={bulkPolling}
                className="w-full mb-3 py-2 rounded-xl bg-gradient-to-r from-gold to-amber-500 text-[#0F172A] font-semibold text-sm disabled:opacity-50"
                data-testid="btn-start-bulk-generate"
              >
                🌊 Queue {bulkSelection.size} Topics →
              </button>
            )}

            {suggestions.length === 0 && (
              <p className="text-white/60 text-sm">Loading...</p>
            )}
            <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="topic-suggestions-list">
              {suggestions.map((s) => {
                const checked = bulkSelection.has(s.id);
                return (
                  <motion.li
                    key={s.id}
                    whileHover={{ scale: 1.01 }}
                    onClick={() => bulkMode ? toggleBulk(s.id) : setForm({ ...form, topic: s.title })}
                    className={`p-3 rounded-lg border cursor-pointer ${
                      checked ? "border-gold bg-gold/10" : "border-gold/20 bg-[#0F172A]/70 hover:border-gold/50"
                    }`}
                    data-testid={`suggestion-${s.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {bulkMode && (
                        <input type="checkbox" checked={checked} readOnly
                               className="mt-0.5 w-4 h-4 accent-gold shrink-0" />
                      )}
                      <p className="text-white text-[13px] font-medium leading-snug flex-1">{s.title}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className={`px-2 py-0.5 text-[10px] rounded-full border ${scoreColor(s.traffic_potential)}`} title="Traffic potential">
                        T {s.traffic_potential}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] rounded-full border ${scoreColor(s.seo_score)}`} title="SEO score">
                        S {s.seo_score}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] rounded-full border ${scoreColor(100 - s.competition_score)}`} title="Competition (lower = better)">
                        C {s.competition_score}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] rounded-full border ${scoreColor(s.trending_score)}`} title="Trending">
                        Tr {s.trending_score}
                      </span>
                      {typeof s.conversion_score === "number" && (
                        <span className={`px-2 py-0.5 text-[10px] rounded-full border ${scoreColor(s.conversion_score)}`} title="Conversion (funnel) score">
                          💰 {s.conversion_score}
                        </span>
                      )}
                      <span className="px-2 py-0.5 text-[10px] rounded-full border border-gold/40 text-gold ml-auto" title="Final weighted score">
                        ★ {s.final_score}
                      </span>
                    </div>
                    <p className="text-[10px] text-white/40 mt-1">
                      {s.category} · {s.source}{s.event_date ? ` · ${s.event_date}` : ""}
                    </p>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ValidationPanel({ validation }) {
  if (!validation) return null;
  const { is_valid, errors = [], warnings = [], stats = {} } = validation;
  return (
    <div className={`rounded-xl border p-3 text-sm ${is_valid ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/10"}`}
         data-testid="validation-panel">
      <div className="flex items-center gap-2 mb-2">
        {is_valid
          ? <ShieldCheck className="w-4 h-4 text-emerald-400" />
          : <AlertTriangle className="w-4 h-4 text-rose-400" />}
        <span className={is_valid ? "text-emerald-300 font-semibold" : "text-rose-300 font-semibold"}>
          {is_valid ? "Fact Validator PASSED" : "Fact Validator FAILED — Publish blocked"}
        </span>
      </div>
      {errors.length > 0 && (
        <ul className="mt-1 space-y-1">
          {errors.map((e, i) => <li key={i} className="text-rose-200 text-[13px]">{e}</li>)}
        </ul>
      )}
      {warnings.length > 0 && (
        <details className="mt-2">
          <summary className="text-amber-300 text-[12px] cursor-pointer">{warnings.length} warnings</summary>
          <ul className="mt-1 space-y-1">
            {warnings.map((w, i) => <li key={i} className="text-amber-200 text-[12px]">{w}</li>)}
          </ul>
        </details>
      )}
      <div className="mt-2 text-[11px] text-white/50">
        Word count: {stats.word_count || "?"} · Shloka boxes: {stats.shloka_boxes_found || 0} · Verified: {stats.shlokas_verified || 0} · JSON-LD: {stats.jsonld_scripts || 0}
      </div>
    </div>
  );
}

/* ============================================================================
   P0 Review Card — Search Intent + Keywords + CTA + Shloka Semantic Verdicts
   ============================================================================ */
function P0ReviewCard({ draft }) {
  if (!draft) return null;
  const intent = draft.intent_profile || {};
  const cta = draft.cta_bundle || {};
  const shloka = draft.shloka_semantic || { verdicts: [], counts: {}, publish_gate: "—" };
  const needsReview = draft.needs_human_review;

  const statusStyle = (s) => {
    const map = {
      PASS: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      WARNING: "bg-amber-500/15 text-amber-300 border-amber-500/40",
      HUMAN_REVIEW: "bg-sky-500/15 text-sky-300 border-sky-500/40",
      FAIL: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    };
    return map[String(s).toUpperCase()] || "bg-white/5 text-white/70 border-white/10";
  };

  return (
    <div
      className="mt-4 p-4 rounded-lg bg-gradient-to-br from-[#0f0508]/70 to-[#15093A]/60 border border-[#D4AF37]/25"
      data-testid="p0-review-card"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-[#D4AF37] tracking-wide">
          🎯 P0 Review — Intent, Keywords, CTA, Shloka
        </h4>
        {needsReview && (
          <span className="text-[10px] px-2 py-1 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/40 font-semibold">
            HUMAN REVIEW
          </span>
        )}
      </div>

      {/* Intent + Category */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-[11px]">
          <div className="text-white/50 uppercase tracking-wider mb-1">Search Intent</div>
          <div className="text-white font-medium">{intent.intent || "—"}</div>
        </div>
        <div className="text-[11px]">
          <div className="text-white/50 uppercase tracking-wider mb-1">Topic Category</div>
          <div className="text-white font-medium">{intent.topic_category || "—"}</div>
        </div>
      </div>

      {/* Primary Keyword */}
      {intent.primary_keyword && (
        <div className="mb-3 text-[12px]">
          <div className="text-white/50 uppercase tracking-wider text-[10px] mb-1">Primary Keyword</div>
          <div className="text-white font-semibold">{intent.primary_keyword}</div>
        </div>
      )}

      {/* Secondary Keywords */}
      {intent.secondary_keywords?.length > 0 && (
        <div className="mb-3">
          <div className="text-white/50 uppercase tracking-wider text-[10px] mb-1.5">Secondary Keywords</div>
          <div className="flex flex-wrap gap-1.5">
            {intent.secondary_keywords.map((k, i) => (
              <span key={i} className="text-[10.5px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-white/80">{k}</span>
            ))}
          </div>
        </div>
      )}

      {/* Long-tail */}
      {intent.long_tail_questions?.length > 0 && (
        <div className="mb-3">
          <div className="text-white/50 uppercase tracking-wider text-[10px] mb-1.5">Long-tail Questions</div>
          <ul className="text-[11.5px] text-white/80 space-y-0.5 list-disc list-inside">
            {intent.long_tail_questions.slice(0, 5).map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}

      {/* CTA */}
      {cta.primary && (
        <div className="mb-3 p-3 rounded-md bg-gold/5 border border-gold/25">
          <div className="text-white/50 uppercase tracking-wider text-[10px] mb-1.5">Selected CTA</div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] font-bold text-emerald-300">PRIMARY:</span>
            <span className="text-[12.5px] text-white font-semibold">{cta.primary.label_hi}</span>
            {cta.primary.is_paid && (
              <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 font-bold">
                ₹{cta.primary.price} PAID
              </span>
            )}
            <span className="text-[10px] text-white/50 font-mono">→ {cta.primary.path}</span>
          </div>
          {cta.secondary && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-bold text-sky-300">SECONDARY:</span>
              <span className="text-[12px] text-white/85">{cta.secondary.label_hi}</span>
              <span className="text-[10px] text-white/50 font-mono">→ {cta.secondary.path}</span>
            </div>
          )}
          {cta.reasoning && (
            <div className="mt-1.5 text-[10px] text-white/45 italic">Reason: {cta.reasoning}</div>
          )}
        </div>
      )}

      {/* Shloka Semantic Verdicts */}
      <div className="pt-3 border-t border-white/[0.06]">
        <div className="flex items-center justify-between mb-2">
          <div className="text-white/50 uppercase tracking-wider text-[10px]">Shloka Verdicts</div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${statusStyle(shloka.publish_gate)}`}>
            Gate: {shloka.publish_gate || "—"}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] mb-2">
          <span className="text-emerald-300">✓ Pass: {shloka.counts?.pass || 0}</span>
          <span className="text-amber-300">⚠ Warning: {shloka.counts?.warning || 0}</span>
          <span className="text-sky-300">◎ Review: {shloka.counts?.human_review || 0}</span>
          <span className="text-rose-300">✗ Fail: {shloka.counts?.fail || 0}</span>
        </div>
        {shloka.verdicts?.length > 0 ? (
          <ul className="space-y-1.5">
            {shloka.verdicts.map((v, i) => (
              <li key={i} className={`p-2 rounded border text-[11px] ${statusStyle(v.status)}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-[10px]">{v.status}</span>
                  <span className="opacity-70 text-[9.5px]">layer={v.layer}</span>
                  {typeof v.confidence === "number" && (
                    <span className="opacity-70 text-[9.5px]">conf={v.confidence.toFixed(2)}</span>
                  )}
                </div>
                {v.sanskrit && <div className="text-[10.5px] opacity-80 mb-0.5">{v.sanskrit}</div>}
                <div className="text-[10.5px] opacity-90">{v.reason}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-[11px] text-white/40 italic">कोई श्लोक validate नहीं हुआ।</div>
        )}
      </div>
    </div>
  );
}
